import type { ArtifactSourceRow } from "@entrilia/shared/artifactRuntime/protocol";
import type {
  SmartViewArtifactSource,
  SmartViewArtifactVersion,
  SmartViewArtifactView,
} from "../../../../../api/types/smartViewArtifactsTypes";

export interface ViewWalkStep {
  viewKey: string;
  params: Record<string, string[]>;
}

const isBlank = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === "string" && value.trim() === "");

const toAnchorSize = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? Math.abs(value) : 0;

const findDimensionColumnKey = (
  version: SmartViewArtifactVersion,
  parent: SmartViewArtifactSource,
  filterKey: string
): string | undefined => {
  const dimensionName = version.filters.find((filter) => filter.key === filterKey)?.dimensionName;
  return parent.columns.find((column) => dimensionName !== undefined && column.dimensionName === dimensionName)?.key;
};

const pickParentRow = (
  rows: ArtifactSourceRow[],
  linkColumnKeys: string[],
  anchorColumnKey: string | undefined
): ArtifactSourceRow | undefined => {
  const candidates = rows.filter((row) => linkColumnKeys.every((key) => !isBlank(row[key])));
  if (anchorColumnKey === undefined) {
    return candidates[0];
  }
  return candidates.reduce<ArtifactSourceRow | undefined>(
    (best, row) => (!best || toAnchorSize(row[anchorColumnKey]) > toAnchorSize(best[anchorColumnKey]) ? row : best),
    undefined
  );
};

const resolveLinkValue = (
  version: SmartViewArtifactVersion,
  filterKey: string,
  parentData: ReadonlyMap<string, ArtifactSourceRow[]>
): string | undefined => {
  const link = version.sources.find((source) => source.link?.filterKeys.includes(filterKey))?.link;
  const parent = link && version.sources.find((source) => source.key === link.parentSourceKey);
  if (!link || !parent) {
    return undefined;
  }
  const columnKey = findDimensionColumnKey(version, parent, filterKey);
  const rows = parentData.get(parent.key);
  if (columnKey === undefined || !rows) {
    return undefined;
  }
  const linkColumnKeys = link.filterKeys.flatMap((key) => findDimensionColumnKey(version, parent, key) ?? []);
  const row = pickParentRow(rows, linkColumnKeys, link.anchor?.parentColumnKey);
  return row ? String(row[columnKey]) : undefined;
};

const resolveViewParams = (
  version: SmartViewArtifactVersion,
  view: SmartViewArtifactView,
  parentData: ReadonlyMap<string, ArtifactSourceRow[]>
): Record<string, string[]> | undefined => {
  const params: Record<string, string[]> = {};
  for (const filterKey of view.linkFilterKeys) {
    const value = resolveLinkValue(version, filterKey, parentData);
    if (value === undefined) {
      return undefined;
    }
    params[filterKey] = [value];
  }
  return params;
};

export const buildViewWalk = (
  version: SmartViewArtifactVersion,
  parentData: ReadonlyMap<string, ArtifactSourceRow[]>
): ViewWalkStep[] =>
  version.views.flatMap((view) => {
    const params = resolveViewParams(version, view, parentData);
    return params ? [{ viewKey: view.key, params }] : [];
  });
